import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

const SEARCH_HISTORY_KEY = 'ticomarket_search_history';
const MAX_HISTORY_ITEMS = 20;

interface SearchTerm {
  term: string;
  count: number;
  lastSearched: string;
}

interface SearchHistoryContextType {
  history: SearchTerm[];
  hasHistory: boolean;
  addSearch: (term: string) => void;
  clearHistory: () => void;
  getTopTerms: (limit?: number) => string[];
}

const SearchHistoryContext = createContext<SearchHistoryContextType | undefined>(undefined);

export const SearchHistoryProvider = ({ children }: { children: ReactNode }) => {
  const [history, setHistory] = useState<SearchTerm[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SearchTerm[];
        setHistory(parsed);
      }
    } catch (error) {
      console.error('Error loading search history:', error);
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  }, []);

  const addSearch = useCallback((term: string) => {
    const normalizedTerm = term.toLowerCase().trim();
    
    if (normalizedTerm.length < 2) return;

    setHistory(currentHistory => {
      const existingIndex = currentHistory.findIndex(
        item => item.term.toLowerCase() === normalizedTerm
      );

      let newHistory: SearchTerm[];

      if (existingIndex >= 0) {
        newHistory = [...currentHistory];
        newHistory[existingIndex] = {
          ...newHistory[existingIndex],
          count: newHistory[existingIndex].count + 1,
          lastSearched: new Date().toISOString(),
        };
      } else {
        const newTerm: SearchTerm = {
          term: normalizedTerm,
          count: 1,
          lastSearched: new Date().toISOString(),
        };
        newHistory = [newTerm, ...currentHistory];
      }

      newHistory.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return new Date(b.lastSearched).getTime() - new Date(a.lastSearched).getTime();
      });

      newHistory = newHistory.slice(0, MAX_HISTORY_ITEMS);

      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error('Error saving search history:', error);
      }

      return newHistory;
    });
  }, []);

  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
      setHistory([]);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }, []);

  const getTopTerms = useCallback((limit: number = 5): string[] => {
    return history.slice(0, limit).map(item => item.term);
  }, [history]);

  return (
    <SearchHistoryContext.Provider
      value={{
        history,
        hasHistory: history.length > 0,
        addSearch,
        clearHistory,
        getTopTerms,
      }}
    >
      {children}
    </SearchHistoryContext.Provider>
  );
};

export const useSearchHistory = () => {
  const context = useContext(SearchHistoryContext);
  if (!context) {
    throw new Error('useSearchHistory must be used within a SearchHistoryProvider');
  }
  return context;
};
